import { isFullLine, type Line, type PartialLine } from '@common/types/Line'
import { mapOrd, rawOrd } from '@lc/libarray'
import {
	createEntityAdapter,
	createSlice,
	type WithSlice,
} from '@reduxjs/toolkit'
import { reducer } from './store'

/* eslint-disable @typescript-eslint/unbound-method */

export const lineAdapter = createEntityAdapter({
	selectId: (line: PartialLine | Line) => line.id,
	sortComparer: mapOrd(rawOrd, line => line.code),
})

declare module './store' {
	// eslint-disable-next-line @typescript-eslint/no-empty-object-type
	export interface LoadedSlices extends WithSlice<typeof lineSlice> {}
}

const { selectById, selectIds, selectEntities } = lineAdapter.getSelectors()

export type LineSlice = ReturnType<(typeof lineSlice)['getInitialState']>
export const lineSlice = createSlice({
	name: 'lines',
	initialState: lineAdapter.getInitialState(),
	reducers: {
		setLine: lineAdapter.setOne,
		setLines: lineAdapter.setMany,

		delLine: lineAdapter.removeOne,
		delLines: lineAdapter.removeMany,

		insertLines: lineAdapter.addMany,

		clearLines: lineAdapter.removeAll,
		initLines: lineAdapter.setAll,
	},
	selectors: {
		getLine: (state, id: string): PartialLine | Line | undefined =>
			selectById(state, id),
		getFullLine: (state, id: string): Line | undefined => {
			const line = selectById(state, id)
			if (!line || !isFullLine(line)) return undefined
			return line
		},
		listLines: selectIds,
		getAllLines: selectEntities,
	},
})

export const {
	reducer: lineReducer,
	actions: {
		setLine,
		setLines,
		delLine,
		delLines,
		insertLines,
		clearLines,
		initLines,
	},
	selectors: { getLine, getFullLine, listLines, getAllLines },
} = lineSlice.injectInto(reducer)
